import { useCallback, useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useIsFetching } from '@zg-rentals/react-query5';

import { Box } from '@zillow/constellation-10';

import LandlordProgressStepper from '../../components/LandlordProgressStepper';
import SectionWrapper from '../../components/SectionWrapper';
import ProgressAndSummaryHeader from '../../components/ProgressAndSummaryHeader';
import { SectionProvider } from '../../contexts/SectionContext';

import { putNodeIdInSearch } from '../../utils/urlUtils';
import Lease from '../../models/Lease';
import { useLeasingUrlParams } from '../../hooks/useLeasingUrlParams';
import { useLeasingStatusForNodeQuery, useLeasingStatusQuery } from '../../utils/queries';

type Props = {
  lease: Lease;
};

function WizardView({ lease }: Props) {
  const history = useHistory();
  const location = useLocation();
  const { leaseId } = useLeasingUrlParams();

  const isFetching = useIsFetching() > 0;

  const searchParams = new URLSearchParams(location.search);
  const nodeId = Number(searchParams.get('nodeId'));

  const previousNodeIdRef = useRef(nodeId);

  const sectionForNodeQuery = useLeasingStatusForNodeQuery(nodeId, leaseId, {
    enabled: !!lease && !!nodeId,
  });

  const sectionQuery = useLeasingStatusQuery(leaseId, {
    enabled: !!lease && !nodeId,
  });

  const section = nodeId ? sectionForNodeQuery.data : sectionQuery.data;

  const getLeasingStatusForNodeAndUpdateUrl = useCallback(() => {
    const nextNodeId = section?.currentNodeId;
    const edgeIsRemoved = section?.currentNodeId !== nodeId;

    if (nextNodeId && nextNodeId !== nodeId) {
      putNodeIdInSearch(`${nextNodeId}`, edgeIsRemoved ? history.replace : history.push);
    }
  }, [section, nodeId, history]);

  useEffect(() => {
    if (!lease) {
      return;
    }

    const nodeIdChanged = previousNodeIdRef.current !== nodeId;
    previousNodeIdRef.current = nodeId;

    if (nodeId && section && nodeIdChanged && !isFetching && section.currentNodeId !== nodeId) {
      getLeasingStatusForNodeAndUpdateUrl();
    }
  }, [lease, nodeId, isFetching, section, getLeasingStatusForNodeAndUpdateUrl]);

  function onLastSectionSubmitted() {
    history.push(Lease.getSummaryPath(leaseId));
  }

  if (!lease) {
    return null;
  }

  return (
    <SectionProvider initialSection={section}>
      <ProgressAndSummaryHeader />
      <Box css={{ marginTop: 'loose' }} />
      <LandlordProgressStepper />
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', marginTop: 'loose' }}>
        <Box css={{ gridColumnEnd: { lg: 'span 1' } }} />
        <Box css={{ gridColumnEnd: { base: 'span 10', lg: 'span 8' } }}>
          <SectionWrapper onLastSectionSubmitted={onLastSectionSubmitted} setNodeIdInSearch />
        </Box>
        <Box css={{ gridColumnEnd: { lg: 'span 1' } }} />
      </Box>
    </SectionProvider>
  );
}

export default WizardView;
